import React, { useState, useEffect } from "react"
import { useGlobalContext } from "../../data/GlobalContext"
import { promotionsData } from "../../data/PromotionData"
import { apiKeyURL } from "../../../App"

export default function ModuleRaceToRiches({ closeModal }) {
  const promoData = promotionsData.find(
    (promo) => promo.title === "Race to Riches"
  )
  const [leaderboardTable, setLeaderboardTable] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { username } = useGlobalContext()
  const [spinshere, setSpinshere] = useState(0)
  const [userInRanking, setUserInRanking] = useState(false)

  // Helper: Convert Base64 string to Uint8Array
  function base64ToUint8Array(base64) {
    const binaryString = window.atob(base64)
    const length = binaryString.length
    const bytes = new Uint8Array(length)
    for (let i = 0; i < length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes
  }

  // Asynchronous decryption function using AES-CBC.
  async function decryptData(encryptedBase64) {
    const key = apiKeyURL
    const iv = new TextEncoder().encode(key)

    try {
      // Import the secret key for AES-CBC decryption
      const cryptoKey = await window.crypto.subtle.importKey(
        "raw",
        new TextEncoder().encode(key),
        { name: "AES-CBC" },
        false,
        ["decrypt"]
      )

      const encryptedData = base64ToUint8Array(encryptedBase64)

      // Decrypt the data
      const decryptedBuffer = await window.crypto.subtle.decrypt(
        { name: "AES-CBC", iv },
        cryptoKey,
        encryptedData
      )
      // Convert ArrayBuffer to string and return it
      return new TextDecoder().decode(decryptedBuffer)
    } catch (error) {
      console.error("Decryption failed:", error)
      return null
    }
  }

  // Parse and structure leaderboard data
  const parseLeaderboardData = (data) => {
    console.debug("Parsing leaderboard data:", data)
    const parsedArray = Object.entries(data).map(([rankingStr, value]) => {
      const [userInfo, pointsAndEncrypted] = value.split("|")
      // Split the points and encrypted string (assumes one comma separation)
      const [pointsStr, encryptedStr] = pointsAndEncrypted
        .split(",")
        .map((s) => s.trim())
      const [userID, playerName] = userInfo.split(",")

      return {
        ranking: Number(rankingStr),
        userID: userID ? userID.trim() : "",
        playerName: playerName ? playerName.trim() : "",
        points: Number(pointsStr),
        encrypted: encryptedStr || "",
        // decrypted will be added later
        decrypted: "",
      }
    })
    console.debug("Parsed leaderboard array:", parsedArray)
    return parsedArray
  }

  // Fetch leaderboard data on component mount
  useEffect(() => {
    const fetchLeaderboardData = async () => {
      try {
        const url = `${process.env.REACT_APP_API_JSON_URL_PROMO}?cmd=getJiliLeaderboard`
        console.debug("Fetching leaderboard data from:", url)
        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        console.debug("Raw API response:", data)
        const parsedData = parseLeaderboardData(data)
        setLeaderboardTable(parsedData)
      } catch (err) {
        console.error("Error fetching leaderboard data:", err)
        setError(err)
      } finally {
        setLoading(false)
      }
    }

    fetchLeaderboardData()
  }, [])

  // Decrypt each item's encrypted field once leaderboard data is loaded
  useEffect(() => {
    async function decryptAll() {
      const updatedData = await Promise.all(
        leaderboardTable.map(async (item) => {
          if (item.encrypted) {
            const decrypted = await decryptData(item.encrypted)
            // Add the decrypted data to the current item
            return { ...item, decrypted }
          }
          return item
        })
      )
      setLeaderboardTable(updatedData)
    }
    if (leaderboardTable.length > 0) {
      decryptAll()
    }
  }, [leaderboardTable])

  useEffect(() => {
    if (!loading && leaderboardTable.length > 0) {
      const userItem = leaderboardTable.find(
        (item) => item.decrypted === username
      )
      if (userItem) {
        setUserInRanking(true)
        const rank = parseInt(userItem.ranking)
        let spins = 0

        // Apply the provided logic to assign spins
        if (rank === 1) spins = 100
        else if (rank === 2) spins = 80
        else if (rank === 3) spins = 50
        else if (rank === 4) spins = 40
        else if (rank === 5) spins = 30
        else if (rank >= 6 && rank <= 20) spins = 20
        else if (rank >= 21 && rank <= 40) spins = 15
        else if (rank >= 41 && rank <= 50) spins = 10

        setSpinshere(spins)
      }
    }
  }, [leaderboardTable, username, loading])

  return (
    <div className="promotion-module-pop-container">
      <div className="promotion-module-container">
        <button onClick={closeModal} className="promotion-close-btn">
          Close
        </button>
        <div className="promotion-module-main">
          <div className="promotion-leaderboard">
            <div className="promotion-module-banner">
              <img src={promoData.imgSrc} alt="Promotion Banner" />
              <div className="img-overlay"></div>
            </div>
            <table className="promotion-module-table">
              <thead>
                <tr>
                  <th>Ranking</th>
                  <th>User ID / Player Name</th>
                  <th>Points</th>
                  {/* <th>Decrypted Data</th> */}
                </tr>
              </thead>
              <tbody>
                {error ? (
                  <tr>
                    <td colSpan="4">
                      Error loading leaderboard data: {error.message}
                    </td>
                  </tr>
                ) : loading ? (
                  <tr>
                    <td colSpan="4">Preparing leaderboard data...</td>
                  </tr>
                ) : leaderboardTable.length > 0 ? (
                  leaderboardTable.map((item) => (
                    <tr key={item.ranking}>
                      <td>{item.ranking}</td>
                      <td>
                        {item.userID} <br />
                        {item.playerName}
                      </td>
                      <td>{item.points.toLocaleString()}</td>
                      {/* <td>{item.decrypted || "Decryption pending"}</td> */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">No leaderboard data available.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="promotion-module-cta">
            {userInRanking ? (
              <span>Congratulations, you get: {spinshere} spins</span>
            ) : (
              <span>Earn your spot to the riches</span>
            )}

            <a href="/slots/?provider=JILI">
              <button className="promotion-module-leaderboard-btn">
                Play Now
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
